import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api-client";

// API response types
interface Leaderboard {
  id: string;
  venueId: string;
  name: string;
  description: string | null;
  period: "weekly" | "monthly" | "alltime";
  status: "active" | "archived";
  sortOrder: "asc" | "desc";
  maxEntries: number;
  createdAt: string;
  updatedAt: string;
}

interface LeaderboardsResponse {
  success: boolean;
  data: {
    items: Leaderboard[];
    pagination: { total: number; page: number; limit: number };
  };
}

// Map API Leaderboard → Campaign shape
export interface Campaign {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  status: "Scheduled" | "Live" | "Ended";
  metric: string;
  topN: number;
  roiUplift: number;
  roiPercent: number;
}

function mapLeaderboardToCampaign(lb: Leaderboard): Campaign {
  const now = new Date();
  const created = new Date(lb.createdAt);
  let startDate: string;
  let endDate: string;

  if (lb.period === "weekly") {
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    startDate = start.toISOString().split("T")[0] ?? "";
    endDate = now.toISOString().split("T")[0] ?? "";
  } else if (lb.period === "monthly") {
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    startDate = start.toISOString().split("T")[0] ?? "";
    endDate = now.toISOString().split("T")[0] ?? "";
  } else {
    startDate = created.toISOString().split("T")[0] ?? "";
    endDate = now.toISOString().split("T")[0] ?? "";
  }

  const status: Campaign["status"] = lb.status === "archived" ? "Ended" : "Live";

  return {
    id: lb.id,
    name: lb.name,
    startDate,
    endDate,
    status,
    metric: lb.description ?? "Score",
    topN: lb.maxEntries,
    roiUplift: 0,
    roiPercent: 0,
  };
}

export function useLeaderboards(venueId: string | null | undefined) {
  return useQuery({
    queryKey: ["leaderboards", venueId],
    queryFn: async () => {
      if (!venueId) return [];
      const response = await apiGet<LeaderboardsResponse>(`/venues/${venueId}/leaderboards`);
      return response.data.items.map(mapLeaderboardToCampaign);
    },
    enabled: !!venueId,
  });
}
